/**
 * Modal dialogs on the native <dialog> element: focus is trapped, Esc
 * cancels, the page behind is inert. No library.
 *
 * `openDialog` is the general form; `confirmDialog` and `promptDialog`
 * replace `window.confirm` / `window.prompt`, which look foreign on the
 * board and are blocked in some embedded browsers.
 */
import { esc } from '../dom';
import './ui.css';

export interface DialogAction {
  label: string;
  /** Value the dialog resolves with when this button is used. */
  value: string;
  kind?: 'primary' | 'ghost' | 'danger';
}

export interface DialogOptions {
  title: string;
  /** HTML string (escape user data!) or an element. Form fields go here. */
  body: string | HTMLElement;
  actions: DialogAction[];
  /**
   * Called on submit with the form and the chosen action. Return false (or
   * a promise of false) to keep the dialog open, e.g. after showing a
   * validation error. Cancel (Esc, or an action with value 'cancel') never
   * calls it.
   */
  onSubmit?: (form: HTMLFormElement, value: string) => boolean | Promise<boolean>;
  /** Extra class on the dialog, for sizing. */
  className?: string;
}

export interface OpenDialog {
  element: HTMLDialogElement;
  form: HTMLFormElement;
  /** Resolves with the action value, or null when cancelled. */
  result: Promise<string | null>;
  /**
   * Resolves once the dialog is really gone. Until then the page behind is
   * inert (a modal dialog): navigate after this, or the next page cannot be
   * typed into for a moment.
   */
  gone: Promise<void>;
  close(value?: string | null): void;
}

let dialogCounter = 0;

export function openDialog(options: DialogOptions): OpenDialog {
  dialogCounter += 1;
  const titleId = `dialog-title-${String(dialogCounter)}`;
  const dialog = document.createElement('dialog');
  dialog.className = `dialog ${options.className ?? ''}`;
  dialog.setAttribute('aria-labelledby', titleId);
  dialog.innerHTML = `
    <form class="dialog__form" novalidate>
      <h2 class="dialog__title" id="${titleId}">${esc(options.title)}</h2>
      <div class="dialog__body"></div>
      <div class="dialog__actions">
        ${options.actions
          .map(
            (a) =>
              `<button class="btn ${a.kind === 'ghost' ? 'btn--ghost' : a.kind === 'danger' ? 'btn--danger' : ''}" value="${esc(a.value)}" ${a.value === 'cancel' ? 'type="button" data-cancel' : 'type="submit"'}>${esc(a.label)}</button>`,
          )
          .join('')}
      </div>
    </form>`;
  const form = dialog.querySelector('form') as HTMLFormElement;
  const body = dialog.querySelector('.dialog__body') as HTMLElement;
  if (typeof options.body === 'string') body.innerHTML = options.body;
  else body.append(options.body);

  let settle: (value: string | null) => void = () => undefined;
  const result = new Promise<string | null>((resolve) => {
    settle = resolve;
  });
  let markGone: () => void = () => undefined;
  const gone = new Promise<void>((resolve) => {
    markGone = resolve;
  });
  let closed = false;
  const close = (value: string | null = null): void => {
    if (closed) return;
    closed = true;
    settle(value);
    let finished = false;
    const finish = (): void => {
      if (finished) return;
      finished = true;
      dialog.close();
      dialog.remove();
      markGone();
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }
    // Shrink away softly, then really close.
    dialog.classList.add('is-closing');
    dialog.addEventListener('animationend', finish, { once: true });
    window.setTimeout(finish, 400);
  };

  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close(null);
  });
  for (const b of dialog.querySelectorAll('[data-cancel]')) {
    b.addEventListener('click', () => {
      close(null);
    });
  }
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const submitter = e.submitter as HTMLButtonElement | null;
    const value =
      submitter?.value ?? options.actions.find((a) => a.value !== 'cancel')?.value ?? 'ok';
    const buttons = [...form.querySelectorAll('button')];
    buttons.forEach((b) => (b.disabled = true));
    void Promise.resolve(options.onSubmit?.(form, value) ?? true)
      .then((ok) => {
        if (ok) close(value);
      })
      .finally(() => {
        buttons.forEach((b) => (b.disabled = false));
      });
  });

  document.body.append(dialog);
  dialog.showModal();
  const firstInput = body.querySelector<HTMLElement>('input, select, textarea');
  (firstInput ?? form.querySelector<HTMLElement>('button[type="submit"]'))?.focus();
  return { element: dialog, form, result, gone, close };
}

export async function confirmDialog(
  message: string,
  options: { title?: string; confirmLabel?: string; danger?: boolean } = {},
): Promise<boolean> {
  const { result } = openDialog({
    title: options.title ?? 'Bist du sicher?',
    body: `<p>${esc(message)}</p>`,
    actions: [
      { label: 'Abbrechen', value: 'cancel', kind: 'ghost' },
      {
        label: options.confirmLabel ?? 'Ja',
        value: 'ok',
        kind: options.danger === true ? 'danger' : 'primary',
      },
    ],
  });
  return (await result) === 'ok';
}

export async function promptDialog(
  label: string,
  options: { title?: string; initial?: string; maxLength?: number; confirmLabel?: string } = {},
): Promise<string | null> {
  const dialog = openDialog({
    title: options.title ?? label,
    body: `<label class="field">${esc(label)}<input name="value" value="${esc(options.initial ?? '')}" maxlength="${String(options.maxLength ?? 100)}" autocomplete="off" /></label>`,
    actions: [
      { label: 'Abbrechen', value: 'cancel', kind: 'ghost' },
      { label: options.confirmLabel ?? 'Speichern', value: 'ok' },
    ],
  });
  const input = dialog.form.querySelector('input') as HTMLInputElement;
  input.select();
  const value = await dialog.result;
  return value === 'ok' ? input.value : null;
}
